import { useState, useEffect } from "react";

import { Edit, Archive, Trash2, Eye, Sparkles, Plus, X, Loader2, Code, FileText, Palette, BookOpen, GitBranch } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { useCrud } from "@/hooks/useCrud";
import { supabase } from "@/integrations/supabase/client";


import { CrudCard } from "./CrudCard";
import { CrudManager } from "./CrudManager";
import { CrudItemBase } from "./types/crud";


interface ConventionPack extends CrudItemBase {
  category: string;
  conventions: string[];
  is_system: boolean;
  created_by?: string;
  parent_pack_id?: string;
  replaced_by_pack_id?: string;
  archived_at?: string;
}

interface UsageStats {
  id: string;
  name: string;
  total_uses: number;
  unique_workspaces: number;
  unique_projects: number;
  unique_users: number;
  uses_last_7_days: number;
  uses_last_30_days: number;
  last_used_at: string | null;
}

const CATEGORY_CONFIG = {
  coding: { label: "Coding", icon: Code, color: "hsl(210, 100%, 60%)" },
  writing: { label: "Writing", icon: FileText, color: "hsl(280, 100%, 60%)" },
  documentation: { label: "Documentation", icon: BookOpen, color: "hsl(160, 100%, 40%)" },
  design: { label: "Design", icon: Palette, color: "hsl(340, 100%, 60%)" },
};

export const ConventionPacksManager = () => {
  const { invokeAdmin, isLoading: isGenerating, isAdmin } = useAdminEdgeFunction();
  const {
    items: packs,
    isLoading: loading,
    createItem: createPack,
    updateItem: updatePack,
    deleteItem: deletePack,
  } = useCrud<ConventionPack>({
    tableName: "convention_packs",
    queryKey: ["convention_packs"],
    order: { column: "created_at", options: { ascending: false } },
  });
  const [usageStats, setUsageStats] = useState<UsageStats[]>([]);
  const [editingPack, setEditingPack] = useState<ConventionPack | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previewPack, setPreviewPack] = useState<ConventionPack | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deletePackId, setDeletePackId] = useState<string | null>(null);
  const [archivePackId, setArchivePackId] = useState<string | null>(null);
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [generationCategory, setGenerationCategory] = useState<string>("coding");
  const [showStats, setShowStats] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'coding',
    conventions: [] as string[],
    is_system: true,
  });

  const [newConvention, setNewConvention] = useState("");

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const { data, error } = await supabase
        .from('convention_pack_usage_stats')
        .select('*')
        .order('total_uses', { ascending: false });

      if (error) throw error;
      setUsageStats(data || []);
    } catch (error: any) {
      console.error('Failed to fetch usage stats:', error);
    }
  };

  const getUsageForPack = (packId: string) => {
    return usageStats.find(stat => stat.id === packId);
  };

  const handleCreateVersion = async (packId: string) => {
    try {
      const pack = packs.find(p => p.id === packId);
      if (!pack) return;

      const { data, error } = await supabase.rpc('create_convention_pack_version', {
        pack_id: packId,
        new_name: pack.name,
        new_description: pack.description,
        new_conventions: pack.conventions as any,
        new_category: pack.category
      });

      if (error) throw error;
      toast.success('New version created successfully');
      fetchUsageStats();
    } catch (error: any) {
      toast.error('Failed to create version: ' + error.message);
    }
  };

  const handleSavePack = async () => {
    if (!formData.name || !formData.description || formData.conventions.length === 0) {
      toast.error('Please fill in all required fields and add at least one convention');
      return;
    }

    if (editingPack) {
      await updatePack({ id: editingPack.id, updates: formData });
    } else {
      await createPack(formData as any);
    }

    setIsDialogOpen(false);
    resetForm();
    fetchUsageStats();
  };

  const handleDelete = async () => {
    if (!deletePackId) return;

    await deletePack(deletePackId);
    setDeletePackId(null);
    fetchUsageStats();
  };

  const handleGeneratePack = async () => {
    if (!generationPrompt.trim()) {
      toast.error('Please describe what conventions you want to generate');
      return;
    }

    const { data, error } = await invokeAdmin('generate-convention-pack', {
      body: {
        prompt: generationPrompt,
        category: generationCategory,
      },
      showErrorToast: true,
    });

    if (error) return;

    if (data?.error) {
      toast.error(data.error);
      return;
    }

    if (data?.pack) {
      setFormData({
        name: data.pack.name,
        description: data.pack.description,
        category: generationCategory,
        conventions: data.pack.conventions,
        is_system: true,
      });
      setGenerationPrompt("");
      setIsDialogOpen(true);
      toast.success('Convention pack generated successfully');
    }
  };

  const openEditDialog = (pack: ConventionPack) => {
    setEditingPack(pack);
    setFormData({
      name: pack.name,
      description: pack.description,
      category: pack.category,
      conventions: [...pack.conventions],
      is_system: pack.is_system,
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPack(null);
    setFormData({
      name: '',
      description: '',
      category: 'coding',
      conventions: [],
      is_system: true,
    });
    setNewConvention("");
  };

  const addConvention = () => {
    if (newConvention.trim()) {
      setFormData({
        ...formData,
        conventions: [...formData.conventions, newConvention.trim()],
      });
      setNewConvention("");
    }
  };

  const removeConvention = (index: number) => {
    setFormData({
      ...formData,
      conventions: formData.conventions.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <CrudManager
        title="Convention Packs Manager"
        description="Manage reusable convention packs for different domains"
        onCreateNew={openCreateDialog}
        createLabel="Create Pack"
        showStats={showStats}
        onToggleStats={() => setShowStats(!showStats)}
        statsSection={
          <div className="space-y-2">
            {usageStats.slice(0, 10).map(stat => (
              <div key={stat.id} className="flex justify-between items-center p-2 border rounded">
                <span className="font-medium">{stat.name}</span>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>Total: {stat.total_uses}</span>
                  <span>7d: {stat.uses_last_7_days}</span>
                  <span>30d: {stat.uses_last_30_days}</span>
                  <span>Workspaces: {stat.unique_workspaces}</span>
                  <span>Projects: {stat.unique_projects}</span>
                </div>
              </div>
            ))}
          </div>
        }
        aiGenerationSection={
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                AI Convention Pack Generator
              </CardTitle>
              <CardDescription>
                Generate a new convention pack using AI
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={generationCategory} onValueChange={setGenerationCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" style={{ color: config.color }} />
                            {config.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="prompt">Description</Label>
                <Textarea
                  id="prompt"
                  placeholder="e.g., 'React best practices for component design' or 'Python coding standards for data science'"
                  value={generationPrompt}
                  onChange={(e) => setGenerationPrompt(e.target.value)}
                  rows={3}
                />
              </div>
              <Button
                onClick={handleGeneratePack}
                disabled={isGenerating || !generationPrompt.trim()}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Pack
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        }
      >
        {packs.map((pack) => {
          const categoryConfig = CATEGORY_CONFIG[pack.category as keyof typeof CATEGORY_CONFIG];
          const CategoryIcon = categoryConfig?.icon || Code;
          const usage = getUsageForPack(pack.id);

          return (
            <CrudCard
              key={pack.id}
              item={pack}
              stats={usage ? {
                total_uses: usage.total_uses,
                unique_users: usage.unique_users,
                uses_last_7_days: usage.uses_last_7_days,
              } : undefined}
              additionalBadges={
                <>
                  <Badge
                    variant="outline"
                    style={{
                      borderColor: categoryConfig?.color,
                      color: categoryConfig?.color,
                    }}
                  >
                    <CategoryIcon className="h-3 w-3 mr-1" />
                    {categoryConfig?.label}
                  </Badge>
                  <Badge variant="secondary">
                    {pack.conventions.length} conventions
                  </Badge>
                  {pack.is_system && (
                    <Badge variant="outline">System</Badge>
                  )}
                </>
              }
              additionalInfo={
                usage && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Used in {usage.unique_workspaces} workspaces • {usage.unique_projects} projects
                  </p>
                )
              }
              actions={[
                {
                  label: '',
                  icon: Eye,
                  onClick: () => {
                    setPreviewPack(pack);
                    setIsPreviewOpen(true);
                  },
                  variant: 'ghost',
                  title: 'Preview',
                },
                {
                  label: 'New Version',
                  icon: GitBranch,
                  onClick: () => handleCreateVersion(pack.id),
                  variant: 'ghost',
                  title: 'Create new version',
                  hidden: !pack.is_latest_version,
                },
                {
                  label: '',
                  icon: Edit,
                  onClick: () => openEditDialog(pack),
                  variant: 'ghost',
                  title: 'Edit',
                },
                {
                  label: '',
                  icon: Trash2,
                  onClick: () => setDeletePackId(pack.id),
                  variant: 'ghost',
                  title: 'Delete',
                },
              ]}
            />
          );
        })}
      </CrudManager>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPack ? 'Edit Convention Pack' : 'Create Convention Pack'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Pack name"
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" style={{ color: config.color }} />
                          {config.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Pack description"
                rows={3}
              />
            </div>
            <div>
              <Label>Conventions</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={newConvention}
                    onChange={(e) => setNewConvention(e.target.value)}
                    placeholder="Add a convention..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addConvention();
                      }
                    }}
                  />
                  <Button type="button" onClick={addConvention} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-[200px] border rounded-md p-2">
                  {formData.conventions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No conventions added yet
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {formData.conventions.map((convention, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-2 p-2 rounded border bg-card"
                        >
                          <span className="flex-1 text-sm">{convention}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeConvention(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_system"
                checked={formData.is_system}
                onChange={(e) => setFormData({ ...formData, is_system: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_system" className="cursor-pointer">
                System pack (available to all users)
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePack}>
                {editingPack ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewPack?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">{previewPack?.description}</p>
            <div>
              <h3 className="font-medium mb-2">Conventions ({previewPack?.conventions.length})</h3>
              <ScrollArea className="h-[400px] border rounded-md p-4">
                <ul className="space-y-2">
                  {previewPack?.conventions.map((convention, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>{convention}</span>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deletePackId} onOpenChange={() => setDeletePackId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Convention Pack</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this convention pack? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
